import { S, announce, type RouteNoticeKind as StateRouteNoticeKind } from './state';
import * as A from './actions';
import { ROUTER_BASENAME } from '../config';
import { DOC_KINDS, DOC_ID_FIELD_BY_KEY, PIPELINE_LABEL } from '../lib/docs';
import {
  parseUrl,
  toUrl,
  toPresenceLoc,
  SEARCH_SCOPE_KEYS,
  isDashboardScreen,
  type AdminScreen,
  type AppLocation,
  type ParsedLocation,
  type PermissionTab,
  type PresenceLocation,
  type SlugResolver,
} from '../lib/location';
import type { DocId, DocPerm, NodeId } from '../lib/types';

/**
 * THE HISTORY CONTROLLER — the only owner of `history` in the app (plan 1.6, Stage 1b).
 *
 * Deliberately NOT react-router: the app is many roots sharing one state through `S`, every
 * route resolves to actions against regions that already exist, and the route model already
 * existed as `myPresenceLoc()`. So this module binds the pure codec (`lib/location.ts`) to the
 * live app: it DERIVES the location from state (`currentAppLocation`), WRITES it to the address
 * bar (`syncUrl`), and APPLIES a URL back onto state (`applyLocation`) by dispatching through
 * the same actions every button already calls.
 *
 * ── THE REENTRANCY LATCH ─────────────────────────────────────────────────────────────────
 * Applying a location calls the very actions that call `syncUrl()`. Without a latch, pressing
 * Back would push the entry it had just popped and the button would never move. `applying`
 * suppresses every URL write for the duration of an apply; afterwards `resyncUrl()` re-states
 * the URL from ACTUAL state with a replace — so a dispatch that was refused (or resolved to a
 * notice) leaves an address bar that tells the truth rather than one that promises a view the
 * app declined to show.
 *
 * ── PUSH vs REPLACE ──────────────────────────────────────────────────────────────────────
 * Navigation (a doc, a node's popup, an admin screen) pushes — Back should genuinely return.
 * Filters replace, debounced, because `applySearch()` runs per keystroke and a history entry
 * per character would destroy the Back button. A write whose URL equals the current one is
 * dropped entirely, which is what makes the six REFRESH-style `openPopup()`/`openDoc()`
 * re-open call sites free: re-opening the same place is not a navigation.
 *
 * ── SLUGS, AND THE CUID FALLBACK ─────────────────────────────────────────────────────────
 * The URL speaks slugs; the state speaks ids. `/api/map` supplies both directions (`nodes[].
 * slug`, `docSlugs`). An entity created THIS SESSION has no slug until the next load — the
 * server generates slugs, the client never invents one — so the id itself stands in (the same
 * "…then the cuid" fallback the backfill uses), and the resolver accepts an id wherever it
 * accepts a slug. That keeps `myPresenceLoc()` — now derived from `currentAppLocation()`, so
 * the presence socket and the URL are provably one model — exactly as informative as it was
 * before this module existed.
 *
 * ── WHAT THE URL NEVER CARRIES ───────────────────────────────────────────────────────────
 * The camera (`centerOnNodes()` is what a node link means — REACT-REWRITE-PLAN.md), edit mode
 * on a node popup, and the AI-doc Context screen (the codec's seven ADMIN_SCREENS have no
 * segment for it; giving it one now would change the vocabulary already-minted links parse
 * against).
 */

const FILTER_DEBOUNCE_MS = 300;

let wired = false;
/** No URL is written until boot has consumed the URL the user arrived on. */
let routerReady = false;
/** The reentrancy latch: true while a parsed location is being applied to state. */
let applying = false;
let filterTimer: ReturnType<typeof setTimeout> | null = null;

/* ------------------------------------------------------------- slug <-> id resolution -- */

/**
 * Reverse lookups, cached on the identity of their source container. `loadMapFromServer()`
 * replaces `S.nodes`/`S.docSlugs` wholesale (state.ts's container contract), which is exactly
 * when a rebuild is due. Entities added since (no slug yet) are covered by the id fallback;
 * entities deleted since are covered by the existence check.
 *
 * THREE PLACES A SLUG CAN RESOLVE, in this order (V0.45.1):
 *   1. a LIVE slug — what the thing is addressed by now;
 *   2. a former address kept working by an alias, after a deliberate re-slug;
 *   3. the cuid, accepted wherever a slug is.
 *
 * The order is the safety property, not a preference: a live address always beats an alias, so
 * if the two ever met — which the server's reserve rule is there to prevent — the thing that
 * lives there now wins, which is the direction that cannot open the wrong page.
 */
let nodeCacheFor: unknown = null;
let nodeBySlug = new Map<string, NodeId>();

function nodeIdBySlug(slug: string): NodeId | null {
  if (nodeCacheFor !== S.nodes) {
    nodeCacheFor = S.nodes;
    nodeBySlug = new Map();
    for (const id in S.nodes) {
      const s = S.nodes[id].slug;
      if (s) nodeBySlug.set(s, id);
    }
  }
  const hit = nodeBySlug.get(slug);
  if (hit && S.nodes[hit]) return hit;
  // A former address. Read straight off the map rather than cached: it is consulted only when
  // a live slug missed, which is the rare path by construction.
  const aliased = S.nodeSlugAliases[slug];
  if (aliased && S.nodes[aliased]) return aliased;
  return S.nodes[slug] ? slug : null; // the cuid fallback: an id is accepted wherever a slug is
}

let docCacheFor: unknown = null;
let docBySlug = new Map<string, DocId>();

function docIdBySlug(slug: string): DocId | null {
  if (docCacheFor !== S.docSlugs) {
    docCacheFor = S.docSlugs;
    docBySlug = new Map();
    for (const id in S.docSlugs) docBySlug.set(S.docSlugs[id], id);
  }
  const hit = docBySlug.get(slug);
  if (hit) return hit;
  const aliased = S.docSlugAliases[slug];
  if (aliased && S.docTitles[aliased] !== undefined) return aliased;
  return S.docTitles[slug] !== undefined ? slug : null; // cuid fallback (docTitles is keyed by id)
}

const resolver: SlugResolver = { nodeIdBySlug, docIdBySlug };

/* --------------------------------------------------------------- state -> AppLocation -- */

/**
 * Where the app is right now, as one `AppLocation` — the URL's source and, through
 * `toPresenceLoc`, the presence socket's.
 *
 * Precedence mirrors what is actually on screen: an open document outranks everything (it is
 * full-screen, and it is `myPresenceLoc()`'s first case, kept identical); an admin screen
 * outranks the popup beneath it; a popup is the map focused on a node. `mode` and a doc's
 * `nodeSlug` are carried in memory for the presence side even where the URL never writes them
 * (see the codec's field notes).
 */
export function currentAppLocation(): AppLocation {
  const loc: AppLocation = { locale: S.currentLocale, view: 'map' };
  if (S.docOpen && S.docCurrentId) {
    loc.view = 'doc';
    loc.docSlug = S.docSlugs[S.docCurrentId] || S.docCurrentId;
    loc.mode = A.docEditorActive() ? 'edit' : 'view'; // what is on screen, not the flag (V0.53.5)
    const nav = S.docNavNodeId ? S.nodes[S.docNavNodeId] : undefined;
    if (nav) loc.nodeSlug = nav.slug || nav.id;
  } else if (S.adminScreen) {
    loc.view = 'admin';
    loc.admin = S.adminScreen;
    if (S.adminScreen === 'permissions' && S.permissionsTab) loc.adminTab = S.permissionsTab;
  } else if (S.popupFor && S.nodes[S.popupFor]) {
    const n = S.nodes[S.popupFor];
    loc.view = 'node';
    loc.nodeSlug = n.slug || n.id;
    loc.mode = S.editing ? 'edit' : 'view';
  }
  // Filters are written only while ACTIVE. An inactive filter is an absent param — "defer to
  // the recipient's stored state" — so a copied URL never freezes somebody's empty search box
  // into an explicit clear.
  if (S.searchQuery) loc.q = S.searchQuery;
  if (S.activeTags.size) loc.tag = [...S.activeTags];
  if (S.searchDocKind) loc.type = S.searchDocKind;
  if (S.activeDocStatus) loc.status = S.activeDocStatus;
  if (S.heatmapOn) loc.heat = 'clearance';
  const on = SEARCH_SCOPE_KEYS.filter((k) => S.searchScope[k]);
  if (on.length !== SEARCH_SCOPE_KEYS.length) loc.scope = on.length ? on : null;
  return loc;
}

/** What the presence socket reports. `myPresenceLoc()` delegates here — one model, two consumers. */
export function presenceLoc(): PresenceLocation {
  return toPresenceLoc(currentAppLocation(), resolver);
}

/* -------------------------------------------------------------------- writing the URL -- */

/**
 * The state moved in a way the URL should reflect.
 *
 * 'nav' commits immediately with a push; 'filter' coalesces keystrokes into one debounced
 * replace. Both are dropped while a location is being APPLIED (the latch) and before boot has
 * consumed the arrival URL — the deep link somebody is still landing on must not be clobbered
 * by the default-state setup that runs first.
 */
export function syncUrl(kind: 'nav' | 'filter'): void {
  if (applying || !routerReady) return;
  if (kind === 'nav') {
    if (filterTimer) {
      clearTimeout(filterTimer);
      filterTimer = null;
    }
    commit('nav');
  } else {
    if (filterTimer) clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      filterTimer = null;
      commit('filter');
    }, FILTER_DEBOUNCE_MS);
  }
}

function commit(kind: 'nav' | 'filter'): void {
  const next = toUrl(currentAppLocation(), ROUTER_BASENAME);
  if (next === location.pathname + location.search) return; // same place: never stack it
  if (kind === 'nav') history.pushState(null, '', next);
  else history.replaceState(null, '', next);
}

/**
 * Re-state the URL from actual state, replacing — never pushing — the current entry.
 *
 * Exported as `restateUrl()` for one caller outside this file: adopting a re-slugged node
 * (V0.45). REPLACE is the whole point there — the user is looking at the same place, it has
 * simply acquired its real address, and pushing would put a dead one in their back button.
 */
function resyncUrl(): void {
  const want = toUrl(currentAppLocation(), ROUTER_BASENAME);
  if (want !== location.pathname + location.search) history.replaceState(null, '', want);
}

/* ------------------------------------------------------------------- the admin screens -- */

/**
 * Which admin surface is open, reported from BOTH ends and idempotent so the two reports
 * cannot fight: the open/close actions state it synchronously (a URL application must see the
 * state it just changed), and each region's own `open` effect states it again — which is what
 * catches the region's internal close buttons and Escape handlers, none of which go through
 * the close action.
 */
export function reportAdminScreen(screen: AdminScreen, open: boolean): void {
  if (open) {
    if (S.adminScreen === screen) return;
    S.adminScreen = screen;
  } else {
    if (S.adminScreen !== screen) return;
    S.adminScreen = null;
  }
  announce();
  syncUrl('nav');
}

/**
 * Which tab of the permissions page is showing (V0.54.5) — the `/admin/permissions/{tab}`
 * segment. Reported by the page's own effect, so a click on a tab is a history entry exactly
 * as a dashboard tab is; idempotent, so the open action having stated it first costs nothing.
 */
export function reportPermissionsTab(tab: PermissionTab): void {
  if (S.permissionsTab === tab) return;
  S.permissionsTab = tab;
  if (S.adminScreen === 'permissions') syncUrl('nav');
}

/** Open by name, through the SAME gated actions the buttons call. False = the gate refused. */
function openAdminScreen(screen: AdminScreen, tab?: PermissionTab): boolean {
  switch (screen) {
    case 'permissions':
      return A.openPermPage(tab);
    // The three dashboard words are TABS of one region (V0.44.1). `openDashboard(tab)` is
    // idempotent — open while open only switches the tab — which is what lets
    // `applyResolvedTarget` below skip the close when both sides are dashboard words.
    case 'dashboard':
      return A.openDashboard('dashboard');
    case 'documents':
      return A.openDashboard('documents');
    // A dashboard word like the other three since V0.49. Gated on SHOW_LOGS *or* AI_ADMIN
    // inside `openDashboard`, not on `openLogs()`'s AI_ADMIN — a deep link held by somebody
    // who may read the action log must open it, and `openLogs()` means the document history.
    case 'logs':
      return A.openDashboard('logs');
    // The Projects tab (V0.50.2): a dashboard word gated on VIEW_TODOS inside `openDashboard`,
    // reached without edit mode for the same reason `logs` is.
    case 'projects':
      return A.openDashboard('projects');
    case 'ai-docs':
      return A.openAiDocModal(); // the AI tab, behind the console's own two-gate rule
    case 'targets':
      return A.openTargets();
    case 'live-verify':
      // A verification run starts FROM a node's AI panel; a bare URL has no node to start
      // from, so the link opens the map and says nothing — there is nothing to gate a notice
      // on that would not be a lie in one direction or the other.
      return true;
  }
}

function closeAdminScreen(): void {
  switch (S.adminScreen) {
    case 'permissions':
      A.closePermPage();
      break;
    case 'dashboard':
    case 'documents':
    case 'projects':
    case 'logs':
    case 'ai-docs':
      A.closeDashboard();
      break;
    case 'targets':
      A.closeTargets();
      break;
    case 'live-verify':
      A.closeLiveVerify();
      break;
  }
}

/* --------------------------------------------------------------- the route notice ------ */

/**
 * The ROUTING half of the banner's vocabulary — the two wordings this module decides between.
 *
 * The field's full type lives in `app/state.ts`, which also carries the concurrency half
 * (`map-refreshed`, `edit-rejected`, set by `app/mapSync.ts`). Written as an `Extract` rather
 * than a second literal union so the two declarations cannot drift: rename one of these in
 * `state.ts` and this narrows to `never`, which makes `setNotice('not-found')` a compile error
 * instead of a banner that silently stops appearing.
 */
export type RouteNoticeKind = Extract<StateRouteNoticeKind, 'not-permitted' | 'not-found'>;

function setNotice(kind: RouteNoticeKind): void {
  S.routeNotice = kind;
  S.noticeDetail = null;
  announce();
}

export function dismissRouteNotice(): void {
  if (!S.routeNotice) return;
  S.routeNotice = null;
  S.noticeDetail = null;
  announce();
}

/* ----------------------------------------------------------- resolving a parsed route -- */

type ResolvedTarget =
  | { kind: 'none' } // refused or unknown: leave the view alone (a notice says why)
  | { kind: 'map' }
  | { kind: 'node'; id: NodeId }
  | { kind: 'doc'; id: DocId; mode: 'view' | 'edit' }
  | { kind: 'admin'; screen: AdminScreen; tab?: PermissionTab };

/**
 * Would opening this document be refused for THIS user?
 *
 * Doorway permissions apply — a doc reachable only through slots this user's group cannot see
 * is not theirs to open.
 *
 * ON THE WORDING, which V0.43 inverted. This used to reason that a hidden owner must answer
 * 'not-permitted' and never 'not-found', so that a link could not be used to probe what
 * exists. That was the best available answer while the browser held every node and merely
 * declined to draw some: 'not-found' would have been a lie the client knew was a lie.
 *
 * Now the server withholds hidden nodes outright, so a hidden owner is not in `S.nodes` at
 * all, `ownerNodeOfDoc` returns nothing, and the honest answer is the one the client can
 * actually give: unknown. Both cases collapse into 'not-found', which is the STRONGER
 * position — there is no longer a wording that distinguishes "exists but not yours" from
 * "never existed", and so no oracle to probe with.
 *
 * The `permHidden` branch below is therefore not dead code: `perm.hiddenNodes` can name a node
 * this payload DID send (the root is exempt from withholding, and the regression suite injects
 * hidden ids into a live page), and that case still deserves the precise wording.
 */
function docRefusal(id: DocId): RouteNoticeKind | null {
  const owner = A.ownerNodeOfDoc(id);
  if (!owner) return null; // an unattached page: nothing to gate on
  if (A.permHidden(owner)) return 'not-permitted';
  const n = S.nodes[owner];
  const kinds: DocPerm[] = [];
  for (const k of DOC_KINDS) {
    if ((n as unknown as Record<string, unknown>)[DOC_ID_FIELD_BY_KEY[k.key]] === id) kinds.push(k.perm);
  }
  if ((n.customDocs || []).includes(id)) kinds.push('custom');
  if (kinds.length && !kinds.some((p) => A.canDocBtn(p))) return 'not-permitted';
  return null;
}

/**
 * Turn a parsed location into a dispatchable target, deciding the notice as a side effect.
 * MUST run after `applyPermissions()` — the hidden sets are what make 'not-permitted' and
 * 'not-found' different answers.
 */
function resolveTarget(loc: ParsedLocation): ResolvedTarget {
  if (loc.problem) {
    setNotice('not-found');
    return { kind: 'none' };
  }
  if (loc.view === 'node' && loc.nodeSlug) {
    const id = nodeIdBySlug(loc.nodeSlug);
    if (!id) {
      setNotice('not-found');
      return { kind: 'none' };
    }
    if (A.permHidden(id)) {
      setNotice('not-permitted');
      return { kind: 'none' };
    }
    return { kind: 'node', id };
  }
  if (loc.view === 'doc' && loc.docSlug) {
    const id = docIdBySlug(loc.docSlug);
    if (!id) {
      setNotice('not-found');
      return { kind: 'none' };
    }
    const refused = docRefusal(id);
    if (refused) {
      setNotice(refused);
      return { kind: 'none' };
    }
    // `/edit` asks for EDIT MODE (V0.53.5 — the editor is the mode's, not the page's), and
    // honours the permission quietly: an account with no editing brick gets the page read-only
    // and no mode; a participant who may not edit THIS page gets edit mode and the reader,
    // exactly as the doc bar's own switch would have given them.
    return {
      kind: 'doc',
      id,
      mode: loc.mode === 'edit' && A.canEnterEdit() ? 'edit' : 'view',
    };
  }
  if (loc.view === 'admin' && loc.admin) return { kind: 'admin', screen: loc.admin, tab: loc.adminTab };
  return { kind: 'map' };
}

/* ------------------------------------------------------------- applying onto the app -- */

// `revealAncestors` moved to app/actions.ts (V0.42.1): its reveal -> relayout -> render ->
// open -> centre idiom was copy-pasted here twice, and MyItems needs it too.

/**
 * Dispatch one resolved target through the existing actions.
 *
 * State that a region announces asynchronously (`popupFor` arrives via a mount effect) is
 * stated synchronously here as well, because `resyncUrl()` runs before any effect and must
 * read the state this dispatch just decided — the region's own announcement then finds
 * nothing left to change.
 */
function applyResolvedTarget(t: ResolvedTarget): { centered: boolean } {
  if (t.kind === 'none') return { centered: false };
  if (t.kind === 'node') {
    if (S.adminScreen) closeAdminScreen();
    if (S.docOpen) A.closeDoc();
    A.revealAncestors(t.id);
    A.autoLayout('view'); // following a link opens branches for the reader, nobody else
    A.render();
    A.openPopup(t.id);
    if (S.popupFor !== t.id) A.setPopupFor(t.id);
    A.centerOnNodes([t.id]);
    return { centered: true };
  }
  if (t.kind === 'doc') {
    if (S.adminScreen) closeAdminScreen();
    if (S.popupFor) {
      A.closePopup();
      A.setPopupFor(null);
    }
    const wantEdit = t.mode === 'edit';
    if (S.docOpen && S.docCurrentId === t.id) {
      // Same page: only the mode can differ. Never re-open what is already exactly open —
      // that is the REFRESH path, and it discards an editor's unsaved draft. A plain address
      // while edit mode is on but THIS page is read-only asks for nothing that is not so.
      if (A.docEditorActive() === wantEdit) return { centered: false };
      if (wantEdit) A.openDocEditor(t.id, S.docNavNodeId);
      else A.leaveDocEditor();
      return { centered: false };
    }
    // A plain address leaves the mode alone — the reader's mode is theirs (V0.53.5); `/edit`
    // enters it.
    if (wantEdit) A.openDocEditor(t.id, null);
    else A.openDoc(A.docUrlFor(t.id), S.docTitles[t.id] || 'Documentation', t.id, null);
    return { centered: false };
  }
  if (t.kind === 'admin') {
    if (S.docOpen) A.closeDoc();
    if (S.popupFor) {
      A.closePopup();
      A.setPopupFor(null);
    }
    if (S.adminScreen !== t.screen) {
      // Two dashboard words are two TABS of one region: switch, never close-and-reopen, or
      // back/forward between /admin/dashboard and /admin/documents would unmount the table.
      const sameRegion = isDashboardScreen(S.adminScreen) && isDashboardScreen(t.screen);
      if (S.adminScreen && !sameRegion) closeAdminScreen();
      if (!openAdminScreen(t.screen, t.tab)) setNotice('not-permitted');
    } else if (t.screen === 'permissions' && t.tab && t.tab !== S.permissionsTab) {
      // Back/forward between two tabs of the open page: switch in place, as the dashboard does.
      A.setPermissionsTab(t.tab);
    }
    return { centered: false };
  }
  // The plain map: close whatever is above it.
  if (S.adminScreen) closeAdminScreen();
  if (S.docOpen) A.closeDoc();
  if (S.popupFor) {
    A.closePopup();
    A.setPopupFor(null);
  }
  return { centered: false };
}

/** The filter params a location carries EXPLICITLY. Absent params defer; they touch nothing. */
function applyFilters(loc: AppLocation): void {
  if (loc.scope !== undefined) {
    const list = loc.scope ?? []; // null = ?scope= — explicitly none
    S.searchScope = {
      name: list.includes('name'),
      desc: list.includes('desc'),
      tags: list.includes('tags'),
      content: list.includes('content'),
    };
    A.syncScopeUI();
  }
  if (loc.type !== undefined) A.setSearchDocKind(loc.type ?? '');
  if (loc.tag !== undefined) A.setActiveTags((loc.tag ?? []).filter((t) => !A.tagHidden(t)));
  if (loc.status !== undefined) {
    // Validated against the CURRENT stage table: a link minted before V0.38 may carry a
    // retired docStatus value, and an unknown key applies as "no filter" rather than a
    // filter nothing can match.
    const v = loc.status && PIPELINE_LABEL[loc.status] ? loc.status : '';
    A.setActiveDocStatus(v);
  }
  // `?heat=clearance` turns the mode on, `?heat=` (explicitly empty) turns it off; setHeatmap
  // self-gates on editing && participant, so a link cannot put a reader into a mode their
  // account has no edit switch for. Quiet — the alert belongs to the explicit toggle alone.
  if (loc.heat !== undefined) A.setHeatmap(loc.heat === 'clearance');
  // Last: applySearch() recomputes the reveal set against the full filter state above.
  if (loc.q !== undefined) A.setSearchText(loc.q);
}

/**
 * Apply a parsed URL onto the running app — the popstate path.
 *
 * A locale change is a map reload and comes first, awaited, so the target resolves against
 * the right language's state. The latch covers the whole apply; the trailing `resyncUrl()`
 * is what handles a refused dispatch — the address bar is re-stated from what the app
 * actually did, so a close that a guard declines re-syncs the URL back.
 */
export async function applyLocation(loc: ParsedLocation): Promise<void> {
  applying = true;
  try {
    if (
      loc.locale &&
      loc.locale !== S.currentLocale &&
      S.languages.some((l) => l.code === loc.locale)
    ) {
      await A.setLocale(loc.locale);
    }
    applyFilters(loc);
    applyResolvedTarget(resolveTarget(loc));
  } finally {
    applying = false;
  }
  resyncUrl();
}

/* ------------------------------------------------------------------------------ boot -- */

/**
 * The boot-time deep link, in the three steps the boot machine orders:
 *
 *  1. `stashRoute()` — top of `enterApp()`, PURE parse only. Its one side effect is the
 *     locale: the URL's segment feeds the first `loadMapFromServer()`, whose response then
 *     carries the server-RESOLVED locale (an unknown segment lands on the default).
 *  2. `resolvePendingRoute()` — the `urlParsed` phase, AFTER permissions: decides target vs
 *     notice without leaking existence, and records which filters the URL explicitly
 *     supplies so `restoreViewState()` knows what not to overwrite.
 *  3. `applyPendingRoute()` — after `viewRestored` and the first layout: applies the filters
 *     and the target, marks the router ready to write URLs, and replaces the arrival URL
 *     with its canonical form (corrected locale included).
 */
export function initRouter(): void {
  if (wired) return; // once-latch, the initChrome() idiom
  wired = true;
  window.addEventListener('popstate', () => {
    // Before boot finishes (or signed out) there is nothing to apply onto; enterApp() will
    // consume whatever URL the session lands on.
    if (S.bootPhase !== 'ready' || !S.currentUser) return;
    void applyLocation(parseUrl(location.pathname, location.search, ROUTER_BASENAME));
  });
}

export function stashRoute(): void {
  const parsed = parseUrl(location.pathname, location.search, ROUTER_BASENAME);
  S.pendingRoute = parsed;
  if (parsed.locale) S.currentLocale = parsed.locale;
}

let pendingTarget: ResolvedTarget = { kind: 'none' };
let explicitFilters = { q: false, tag: false, type: false, status: false, scope: false, heat: false };

export function resolvePendingRoute(): void {
  const r = S.pendingRoute;
  explicitFilters = {
    q: r?.q !== undefined,
    tag: r?.tag !== undefined,
    type: r?.type !== undefined,
    status: r?.status !== undefined,
    scope: r?.scope !== undefined,
    heat: r?.heat !== undefined,
  };
  pendingTarget = r ? resolveTarget(r) : { kind: 'none' };
}

/** Which filters the arrival URL set explicitly — what the view restore must not overwrite. */
export function urlFilterOverrides(): Readonly<typeof explicitFilters> {
  return explicitFilters;
}

export function applyPendingRoute(): { centered: boolean } {
  const r = S.pendingRoute;
  S.pendingRoute = null;
  const target = pendingTarget;
  pendingTarget = { kind: 'none' };
  let centered = false;
  applying = true;
  try {
    if (r) applyFilters(r);
    // 'map' at boot would only close things that cannot be open yet; skip the no-op.
    if (target.kind !== 'none' && target.kind !== 'map') {
      centered = applyResolvedTarget(target).centered;
    }
  } finally {
    applying = false;
  }
  routerReady = true;
  resyncUrl();
  return { centered };
}

/* -------------------------------------------------------- presence avatars (plan 1.7) -- */

/**
 * Jump to where a colleague is — the presence stack's avatar click, and deliberately the
 * SECOND consumer of the same navigation this module already does for URLs.
 *
 * A document opens in YOUR mode (V0.53.5: a colleague's editing is theirs, and the one opener
 * decides what yours shows); a node centres and opens its popup; the plain map is a no-op. A
 * location this user may not see refuses silently — the avatar's tooltip already says where
 * they are, and a notice would repeat it.
 */
export function openPresenceLoc(
  loc: { nodeId?: NodeId | null; docId?: string | null } | null | undefined,
): void {
  if (!loc) return;
  if (loc.docId) {
    const id = loc.docId;
    if (S.docTitles[id] === undefined) return; // not a document this client knows it may see
    if (docRefusal(id)) return;
    A.openDoc(
      A.docUrlFor(id),
      S.docTitles[id] || 'Documentation',
      id,
      loc.nodeId && S.nodes[loc.nodeId] ? loc.nodeId : null,
    );
    return;
  }
  if (loc.nodeId) {
    const id = loc.nodeId;
    if (!S.nodes[id] || A.permHidden(id)) return;
    A.revealAncestors(id);
    A.autoLayout('view'); // as above: a navigation reveals a branch, it does not edit one
    A.render();
    A.openPopup(id);
    A.centerOnNodes([id]);
  }
}

/**
 * Re-state the URL from actual state — the exported name for `resyncUrl()`.
 *
 * One caller: `adoptNodeSlug()` in actions.ts, after a node's placeholder slug is replaced by
 * one built from its first real name (V0.45). Until then the address bar showed the old slug,
 * or the cuid fallback, for a node the user had just named.
 */
export const restateUrl = (): void => resyncUrl();
